// FROM https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/TextField/TextFieldContextMenu/index.tsx

import { Box } from '@modusign/design-system';
import { FontType, TextAlign } from '@modusign/react-signing/types';
import { MouseEvent as ReactMouseEvent, useCallback } from 'react';

import { OnOffButton } from '@app/components/Buttons';
import {
  ContextMenuGroup,
  ContextMenuRow,
  ContextMenuRowDivider,
  ContextMenuWrapper,
} from '@app/components/ContextMenu';
import ContextMenuRowParticipantName from '@app/components/ContextMenuRowParticipantName';
import DataLabelInput from '@app/components/DataLabelInput';
import ContextMenuSelect from '@app/features/apply-react-signing/components/FieldSetupStep/ContextMenuSelect';
import ContextMenuTextAlignRow from '@app/features/apply-react-signing/components/FieldSetupStep/ContextMenuTextAlignRow';

import {
  DATE_FORMAT_OPTIONS,
  DATE_FORMAT_PARTS,
  DateFormatPart,
  DateFormatValue,
  FONT_POINT_OPTIONS,
  FONT_TYPE_OPTIONS,
} from '../constants';

export interface DateFieldContextMenuProps {
  dataLabel: string;
  dataLabelErrorMessage: string;
  fontFamily: FontType;
  dateSuffix: DateFormatPart;
  dateFormat: DateFormatValue;
  fontSize: number;
  isRequired: boolean;
  participantName?: string;
  participantOrder?: number;
  textAlign: TextAlign;
  isDataLabelEditable?: boolean;
  onDataLabelChange: (value: string) => void;
  onFontFamilyChange: (fontFamily: FontType) => void;
  onFontSizeChange: (fontSize: number) => void;
  onRequiredToggle: (
    e: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
    nextIsRequired: boolean
  ) => void;
  onDateFormatPartChange: (dateFormatPart: DateFormatPart) => void;
  onDateFormatChange: (format: DateFormatValue) => void;
  onTextAlignChange: (textAlign: TextAlign) => void;
}

function DateFieldContextMenu({
  dataLabel,
  dataLabelErrorMessage,
  fontFamily,
  fontSize,
  isRequired,
  participantName,
  participantOrder,
  textAlign,
  isDataLabelEditable,
  dateSuffix,
  dateFormat,
  onDataLabelChange,
  onFontFamilyChange,
  onFontSizeChange,
  onRequiredToggle,
  onDateFormatPartChange,
  onTextAlignChange,
  onDateFormatChange,
}: DateFieldContextMenuProps) {
  const handleFontFamilyChange = useCallback(
    (fontFamily: FontType) => {
      onFontFamilyChange(fontFamily);
    },
    [onFontFamilyChange]
  );

  const handleFontSizeChange = useCallback(
    (fontSize: number) => {
      onFontSizeChange(fontSize);
    },
    [onFontSizeChange]
  );

  const handleDateFormatPartChange = useCallback(
    (dateSuffix: DateFormatPart) => {
      onDateFormatPartChange(dateSuffix);
    },
    [onDateFormatPartChange]
  );

  const handleRequiredChange = useCallback(
    (e: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
      onRequiredToggle(e, !isRequired);
    },
    [isRequired, onRequiredToggle]
  );

  return (
    <ContextMenuWrapper>
      {participantName && participantOrder !== undefined && (
        <>
          <ContextMenuRowParticipantName
            participantName={participantName}
            participantOrder={participantOrder}
          />
          <ContextMenuRowDivider />
        </>
      )}
      <ContextMenuRow>
        <b>필수 입력</b>
        <OnOffButton
          active={isRequired}
          data-testid="date-field-required-btn"
          style={{ float: 'right' }}
          onClick={handleRequiredChange}
        />
      </ContextMenuRow>
      <ContextMenuRowDivider />
      <ContextMenuRow>
        <ContextMenuGroup>
          <ContextMenuSelect
            menuName="글자 크기"
            options={FONT_POINT_OPTIONS}
            selectBoxProps={{
              width: '84px',
            }}
            selectedOptionIndex={FONT_POINT_OPTIONS.findIndex(
              (option) => option.value === fontSize
            )}
            onOptionChange={(option) => handleFontSizeChange(option.value)}
          />
          <ContextMenuSelect
            menuName="글자체"
            options={FONT_TYPE_OPTIONS}
            selectBoxProps={{
              width: '84px',
            }}
            selectedOptionIndex={FONT_TYPE_OPTIONS.findIndex(
              (option) => option.value === fontFamily
            )}
            onOptionChange={(option) => handleFontFamilyChange(option.value)}
          />
          <ContextMenuTextAlignRow selectedTextAlign={textAlign} onChange={onTextAlignChange} />
        </ContextMenuGroup>
      </ContextMenuRow>
      <ContextMenuRowDivider />
      <ContextMenuRow>
        <ContextMenuGroup>
          <ContextMenuSelect
            menuName="형식"
            options={DATE_FORMAT_OPTIONS}
            selectBoxProps={{
              width: '120px',
            }}
            selectedOptionIndex={DATE_FORMAT_OPTIONS.findIndex(
              (option) => option.value === dateFormat
            )}
            onOptionChange={(option) => onDateFormatChange(option.value)}
          />
          <ContextMenuSelect
            menuName="구분"
            options={DATE_FORMAT_PARTS}
            selectBoxProps={{
              width: '120px',
            }}
            selectedOptionIndex={DATE_FORMAT_PARTS.findIndex(
              (option) => option.value === dateSuffix.value
            )}
            onOptionChange={(option) => handleDateFormatPartChange(option)}
          />
        </ContextMenuGroup>
      </ContextMenuRow>
      {isDataLabelEditable && (
        <>
          <ContextMenuRowDivider />
          <Box pb="20px" pt="10px" textAlign="left">
            <DataLabelInput
              errorMessage={dataLabelErrorMessage}
              value={dataLabel}
              onChange={onDataLabelChange}
            />
          </Box>
        </>
      )}
    </ContextMenuWrapper>
  );
}

export default DateFieldContextMenu;
